import api from '@/services/apiService';

export interface PickleballAccess {
  canEdit: boolean;
  canRead: boolean;
}

export interface PickleballSetScore {
  setNumber: number;
  scoreSideA: number;
  scoreSideB: number;
  isComplete: boolean;
}

export interface PickleballGamePlayerItem {
  participantId: number;
  playerName: string;
  avatarUrl?: string;
  side: 'a' | 'b';
  slotOrder: number;
}

export interface PickleballGameListItem {
  id: number;
  slug: string;
  sessionId: number;
  sessionSlug: string;
  sessionTitle: string;
  sessionType: string;
  rotationFormat?: string;
  roundNumber?: number;
  playerFormat: string;
  scoringType: string;
  maxSets: number;
  status: string;
  winnerSide?: string;
  sideAPlayers: PickleballGamePlayerItem[];
  sideBPlayers: PickleballGamePlayerItem[];
  setScores: PickleballSetScore[];
}

export interface PickleballSessionListItem {
  id: number;
  slug: string;
  title: string;
  sessionType: string;
  playerFormat: string;
  rotationFormat: string;
  scoringType: string;
  defaultMaxSets: number;
  status: string;
  createdAt?: string;
  games?: PickleballGameListItem[];
}

export interface PickleballStandingItem {
  position: number;
  participantId: number;
  playerName: string;
  avatarUrl?: string;
  gamesPlayed: number;
  wins: number;
  losses: number;
  winRate: number;
  setsWon: number;
  setsLost: number;
  pointsScored: number;
  pointsConceded: number;
}

export interface PickleballGameDetail extends PickleballGameListItem {
  eventSlug: string;
  canEdit: boolean;
}

export interface PickleballServiceOptions {
  isClient?: boolean;
}

function eventWritePrefix(isClient: boolean): string {
  return isClient ? '/mabar/customer/events' : '/mabar/events';
}

function gameWritePrefix(isClient: boolean): string {
  return isClient ? '/mabar/customer/pickleball' : '/mabar/pickleball';
}

export function createPickleballService(options: PickleballServiceOptions = {}) {
  const isClient = Boolean(options.isClient);

  return {
    async getAccess(eventSlug: string): Promise<PickleballAccess> {
      const res = await api.get(`${eventWritePrefix(isClient)}/${eventSlug}/pickleball/access`);
      return res.data.data;
    },

    async listSessions(eventSlug: string): Promise<PickleballSessionListItem[]> {
      const res = await api.get(`/mabar/events/${eventSlug}/pickleball/sessions`);
      return res.data.data ?? [];
    },

    async getStandings(eventSlug: string): Promise<PickleballStandingItem[]> {
      const res = await api.get(`/mabar/events/${eventSlug}/pickleball/standings`);
      return res.data.data ?? [];
    },

    async createManualGame(
      eventSlug: string,
      payload: {
        playerFormat: string;
        scoringType?: string;
        maxSets?: number;
        sideA: number[];
        sideB: number[];
      }
    ): Promise<PickleballGameDetail> {
      const res = await api.post(
        `${eventWritePrefix(isClient)}/${eventSlug}/pickleball/sessions/manual-game`,
        payload
      );
      return res.data.data;
    },

    async getGame(gameSlug: string): Promise<PickleballGameDetail> {
      const res = await api.get(`/mabar/pickleball/games/${gameSlug}`);
      return res.data.data;
    },

    async updateScores(
      gameSlug: string,
      sets: { setNumber: number; scoreSideA: number; scoreSideB: number }[]
    ): Promise<PickleballGameDetail> {
      const res = await api.patch(`${gameWritePrefix(isClient)}/games/${gameSlug}/scores`, { sets });
      return res.data.data;
    },

    async updateGameLineup(
      gameSlug: string,
      payload: { sideA: number[]; sideB: number[] }
    ): Promise<PickleballGameDetail> {
      const res = await api.patch(`${gameWritePrefix(isClient)}/games/${gameSlug}/lineup`, payload);
      return res.data.data;
    },

    async deleteGame(gameSlug: string): Promise<void> {
      await api.delete(`${gameWritePrefix(isClient)}/games/${gameSlug}`);
    },
  };
}

const pickleballService = createPickleballService();

export default pickleballService;
